import React from 'react';
import classnames from 'classnames';
import { TextLink, Icon, RichText } from '../../index';
import { SocialMediaNavigation, SocialMediaNavigationItem } from './social-media-links';
import { ButtonType, LinkType } from '../types';
import styles from './footer.module.scss';
import { withSetting } from '../../utils';

export type LanguageSwitchType = {
  options: {
    label: string;
    value: string;
  }[];
  value: string;
  onChange: React.InputHTMLAttributes<HTMLSelectElement>['onChange'];
  label: string;
};

export type FooterType = {
  headline?: string;
  subHeadline?: string;
  logoLink: LinkType;
  logoSrc: string;
  socialMediaNavigationLabel: string;
  socialMediaNavigationItems: SocialMediaNavigationItem[];
  cta?: LinkType;
  linkGroup1Title?: string;
  linkGroup1?: LinkType[];
  linkGroup2Title?: string;
  linkGroup2?: LinkType[];
  linkGroup3Title?: string;
  linkGroup3?: LinkType[];
  copyText?: string;
  languageSwitch?: LanguageSwitchType;
  countryLink?: LinkType & { ariaLabel: string };
  cookieConsentButton?: ButtonType;
  copyright?: string;
  termsAndStatements: LinkType[];
  className?: string;
};

const baseClass = 'footer';

const renderLinkList = (list: LinkType[], title?: string) => (
  <ul className={classnames(styles['navigation__list'])}>
    <li className={classnames(styles['navigation__list__item'], styles['navigation__list__item--title'])}>
      <span>{title}</span>
    </li>
    {list.map((link, index) => (
      <li key={link.label + index} className={styles['navigation__list__item']}>
        <TextLink {...link} variant="hyperlink-200" className={styles['navigation__link']} linkText={link.label} />
      </li>
    ))}
  </ul>
);

const renderTermsAndCookie = (termsAndStatement: LinkType[], cookieConsentButton?: ButtonType) => {
  type Term = LinkType & {
    type: 'term';
  };
  type Cookie = ButtonType & {
    type: 'cookie';
  };
  type Divider = {
    type: 'divider';
  };
  type TermAndCookie = Term | Cookie | Divider;
  type TermAndCookieList = Array<TermAndCookie>;
  const interleave = (arr: TermAndCookie[], thing: Divider) =>
    ([] as TermAndCookieList).concat(...arr.map((n) => [n, thing])).slice(0, -1);

  let terms: TermAndCookieList = termsAndStatement.map((t) => ({ type: 'term', ...t }));
  terms = terms.concat(cookieConsentButton ? { type: 'cookie', ...cookieConsentButton } : []);

  const termsWithDivider = interleave(terms, { type: 'divider' });

  return termsWithDivider.map((t, i) => {
    switch (t.type) {
      case 'term':
      default:
        return (
          <TextLink key={`t.label-${i}`} {...t} variant="HYP-200" className={styles['terms-link']} linkText={t.label} />
        );
      case 'cookie':
        return (
          <button key={`t.label-${i}`} onClick={t.onClick} className={styles.consentButton}>
            {t.label}
          </button>
        );
      case 'divider':
        return <div key={`divider-${i}`} className={styles['terms-divider']}></div>;
    }
  });
};

const FooterComponent: React.FC<FooterType> = ({
  headline,
  subHeadline,
  cta,
  linkGroup1Title,
  linkGroup1,
  linkGroup2Title,
  linkGroup2,
  linkGroup3Title,
  linkGroup3,
  socialMediaNavigationLabel,
  socialMediaNavigationItems,
  countryLink,
  languageSwitch,
  copyText,
  className,
  cookieConsentButton,
  copyright,
  termsAndStatements
}) => {
  const classes = classnames(styles[baseClass], className);

  return (
    <footer className={classes} data-nosnippet="true" data-testid="footer-component">
      <div className={styles['upper-content']}>
        {/* <div className={styles[`${baseClass}__logo`]}>
          <a
            href={logoLink.href}
            rel={logoLink.rel}
            onClick={logoLink.onClick}
            target={logoLink.target}
            aria-label={logoLink.label}
          >
            <img src={logoSrc} alt={logoLink.label} loading="lazy" height="95" width="70" />
          </a>
        </div> */}

        <div className={styles[`${baseClass}__cta-wrapper`]}>
          {headline && <div className={styles[`${baseClass}__headline`]}>{headline}</div>}
          {subHeadline && <div className={styles[`${baseClass}__subHeadline`]}>{subHeadline}</div>}
          {cta && (
            <div>
              <TextLink
                {...cta}
                variant="button-secondary"
                icon={{ name: 'arrow-right--medium', 'aria-hidden': true }}
                iconPosition="right"
                size="300"
                className={styles[`${baseClass}__cta`]}
                linkText={cta.label}
              />
            </div>
          )}
        </div>
        {(linkGroup1 || linkGroup2 || linkGroup3) && (
          <nav className={styles['navigation']}>
            {linkGroup1 && renderLinkList(linkGroup1, linkGroup1Title)}
            {linkGroup2 && renderLinkList(linkGroup2, linkGroup2Title)}
            {linkGroup3 && renderLinkList(linkGroup3, linkGroup3Title)}
          </nav>
        )}
      </div>
      <div className={styles[`${baseClass}__switches-and-socialnav-wrapper`]}>
        <div>
          <div className={styles['language-country']}>
            {countryLink && (
              <div className={styles['country-link']}>
                <Icon name="dealer-locator-location--small" aria-hidden className={styles['country-link__icon']} />
                <a
                  href={countryLink.href}
                  onClick={countryLink.onClick}
                  className={styles[`country-link__anchor`]}
                  aria-label={countryLink.ariaLabel}
                >
                  {countryLink.label}
                </a>
              </div>
            )}
            {languageSwitch && (
              <div className={styles['language-switch']}>
                <select
                  className={styles['language-switch__select']}
                  aria-label={languageSwitch.label}
                  onChange={languageSwitch.onChange}
                  value={languageSwitch.value}
                >
                  {languageSwitch.options.map((option, i) => (
                    <option value={option.value} key={option.value + i} className={styles['language-switch__option']}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Icon name="chevron-down--small" aria-hidden className={styles['language-switch__icon']} />
              </div>
            )}
          </div>
          {copyText && <RichText className={styles['copy-text']} mdString={copyText} />}
        </div>
        <SocialMediaNavigation ariaLabel={socialMediaNavigationLabel} items={socialMediaNavigationItems} />
      </div>
      <div className={styles['divider']}></div>
      <div className={styles['copyright-and-terms']}>
        <div className={styles['copyright']}>{copyright || ''}</div>
        {(termsAndStatements.length > 0 || cookieConsentButton) && (
          <div>{renderTermsAndCookie(termsAndStatements, cookieConsentButton)}</div>
        )}
      </div>
    </footer>
  );
};

export const Footer = withSetting(FooterComponent, 'inverted');
